import { Bound } from '@blocksuite/global/gfx';
import {
  CANVAS_SECTOR_SIZE,
  CANVAS_WORLD_BOUNDS,
  type CanvasWorld,
} from '@blocksuite/std/gfx';

/** The expanded map of a bounded world is the world; an unbounded one (a
 * projection) has no world, so both sizes show its content. */
export function minimapWorldBound(
  content: Bound | null,
  viewport: Bound,
  expanded: boolean,
  aspectRatio: number,
  world: CanvasWorld
) {
  if (expanded && world === 'bounded') return Bound.from(CANVAS_WORLD_BOUNDS);
  // Include the current viewport, then expose the entire rectangular map area
  // instead of clipping wide viewports to a narrow content-shaped rectangle.
  const anchor = content?.unite(viewport) ?? viewport;
  let w = Math.max(anchor.w, viewport.w * 1.25, 1);
  let h = Math.max(anchor.h, viewport.h * 1.25, 1);
  if (w / h < aspectRatio) {
    w = h * aspectRatio;
  } else {
    h = w / aspectRatio;
  }
  return new Bound(
    anchor.x + (anchor.w - w) / 2,
    anchor.y + (anchor.h - h) / 2,
    w,
    h
  );
}

/** World sectors and signed coordinates at the given strength (0–1); no window
 * chrome or footer. An unbounded world has sectors only: without a world there
 * is no equator and no signed coordinate. The equator belongs to the line
 * grid, as on the canvas. */
export function paintMinimapWorld(
  ctx: CanvasRenderingContext2D,
  world: Bound,
  scale: number,
  offsetX: number,
  offsetY: number,
  color: string,
  strength: number,
  canvasWorld: CanvasWorld,
  equator: boolean
) {
  const width = world.w * scale,
    height = world.h * scale;
  const sector = CANVAS_SECTOR_SIZE;
  const x = (value: number) => offsetX + (value - world.x) * scale;
  const y = (value: number) => offsetY + (value - world.y) * scale;
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = 0.5;
  ctx.globalAlpha = 0.2 * strength;
  ctx.beginPath();
  for (
    let value = Math.ceil(world.x / sector) * sector;
    value <= world.x + world.w;
    value += sector
  ) {
    ctx.moveTo(x(value), offsetY);
    ctx.lineTo(x(value), offsetY + height);
  }
  for (
    let value = Math.ceil(world.y / sector) * sector;
    value <= world.y + world.h;
    value += sector
  ) {
    ctx.moveTo(offsetX, y(value));
    ctx.lineTo(offsetX + width, y(value));
  }
  ctx.stroke();
  if (canvasWorld === 'unbounded') {
    ctx.restore();
    return;
  }
  if (equator) {
    ctx.globalAlpha = 0.65 * strength;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x(0), offsetY);
    ctx.lineTo(x(0), offsetY + height);
    ctx.moveTo(offsetX, y(0));
    ctx.lineTo(offsetX + width, y(0));
    ctx.stroke();
  }
  ctx.globalAlpha = 0.85 * strength;
  ctx.fillStyle = color;
  ctx.font = '9px ui-monospace, monospace';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  const stride = Math.max(4, Math.ceil(28 / (sector * scale) / 4) * 4);
  const label = (value: number) =>
    `${value < 0 ? '-' : '+'}${String(Math.abs(value)).padStart(2, '0')}`;
  for (
    let i = Math.ceil(world.x / sector / stride) * stride;
    i * sector <= world.x + world.w;
    i += stride
  ) {
    ctx.fillText(label(i), x(i * sector), 1);
  }
  for (
    let i = Math.ceil(world.y / sector / stride) * stride;
    i * sector <= world.y + world.h;
    i += stride
  ) {
    ctx.save();
    ctx.translate(1, y(i * sector));
    ctx.rotate(-Math.PI / 2);
    ctx.fillText(label(-i), 0, 0);
    ctx.restore();
  }
  ctx.restore();
}
